// src/pages/CompleteGuestInfoPage.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Restaurant as RestaurantIcon,
  LocalBar as LocalBarIcon,
  Spa as SpaIcon,
  FitnessCenter as FitnessCenterIcon,
  Movie as MovieIcon,
  ShoppingBag as ShoppingBagIcon,
  Explore as ExploreIcon,
  Egg as EggIcon,
  LocalFlorist as LocalFloristIcon,
  AcUnit as AcUnitIcon,
  LocalDrink as LocalDrinkIcon,
  BakeryDining as BakeryDiningIcon,
  SetMeal as SetMealIcon,
  LunchDining as LunchDiningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { countries } from '../shared/countries';
import { validateEmail, validateTCKN, validatePassport, isTurkishNationality, isGuestProfileIncomplete } from '../shared/validators';

// Product interests with icons and labels
const productInterestOptions = [
  { value: 'food', label: 'Food', icon: <RestaurantIcon />, color: '#f59e0b' },
  { value: 'beverage', label: 'Beverage', icon: <LocalBarIcon />, color: '#8b5cf6' },
  { value: 'spa', label: 'Spa', icon: <SpaIcon />, color: '#ec4899' },
  { value: 'fitness', label: 'Fitness', icon: <FitnessCenterIcon />, color: '#10b981' },
  { value: 'entertainment', label: 'Entertainment', icon: <MovieIcon />, color: '#3b82f6' },
  { value: 'shopping', label: 'Shopping', icon: <ShoppingBagIcon />, color: '#ef4444' },
  { value: 'tours', label: 'Tours', icon: <ExploreIcon />, color: '#06b6d4' },
];

// Allergies with icons and labels
const allergyOptions = [
  { value: 'eggs', label: 'Eggs', icon: <EggIcon />, color: '#f59e0b' },
  { value: 'flowers', label: 'Flowers', icon: <LocalFloristIcon />, color: '#ec4899' },
  { value: 'nuts', label: 'Nuts', icon: <AcUnitIcon />, color: '#8b5cf6' },
  { value: 'dairy', label: 'Dairy', icon: <LocalDrinkIcon />, color: '#3b82f6' },
  { value: 'gluten', label: 'Gluten', icon: <BakeryDiningIcon />, color: '#f97316' },
  { value: 'seafood', label: 'Seafood', icon: <SetMealIcon />, color: '#06b6d4' },
  { value: 'soy', label: 'Soy', icon: <LunchDiningIcon />, color: '#10b981' },
];

const CompleteGuestInfoPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const paperBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const paperBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const primaryTextColor = isDark ? '#f8fafc' : '#1f2937';
  const secondaryTextColor = isDark ? 'rgba(248, 250, 252, 0.75)' : '#6b7280';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUserProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const hotelId = searchParams.get('hotelId');
  const reservationId = searchParams.get('reservationId');
  const profileId = searchParams.get('profileId');
  const targetProfileId = searchParams.get('targetProfileId'); // ProfileId of the guest to complete
  const surname = searchParams.get('surname');
  const verifyFlow = searchParams.get('verify') === '1';
  const redirectToChatParam = searchParams.get('redirectToChat') === 'true';
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [targetGuestName, setTargetGuestName] = useState<string>('');
  const [missingFieldsDialogOpen, setMissingFieldsDialogOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [isNationalIdFromBackend, setIsNationalIdFromBackend] = useState(false);
  const [isPassportIdFromBackend, setIsPassportIdFromBackend] = useState(false);
  const [otherIncompleteGuest, setOtherIncompleteGuest] = useState<{
    firstName: string;
    lastName: string;
    profileId: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    birthdate: '',
    nationalId: '',
    nationality: '',
    country: '',
    city: '',
    postCode: '',
    sex: '',
    passportId: '',
    productInterests: [] as string[],
    allergies: [] as string[],
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Fetch user data to pre-fill the form
  React.useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingData(true);
      try {
        let userData: any = null;
        let gscData: any = null;

        if (targetProfileId) {
          // Fetch another guest's data by profileId
          // Get all users in the same reservation and find the one with matching profileId
          if (reservationId) {
            try {
              const usersResponse = await api.get('/users/reservation-guests', {
                params: { reservationId: reservationId }
              });

              const roomGuests = usersResponse.data || [];
              const targetGuest = roomGuests.find((guest: any) =>
                guest.profileId === targetProfileId && guest.role === 'guest'
              );

              if (targetGuest) {
                userData = targetGuest;
                setTargetGuestName(`${targetGuest.firstName || ''} ${targetGuest.lastName || ''}`.trim() || 'Guest');
              }

              // Also fetch from GSC API to get complete data
              try {
                const gscResponse = await api.get(`/users/guest-profile-from-gsc/${targetProfileId}`, {
                  params: { reservationId: reservationId }
                });
                gscData = gscResponse.data;
              } catch (error) {
                console.error('Error fetching GSC profile data:', error);
                // Continue without GSC data
              }
            } catch (error) {
              console.error('Error fetching target guest data:', error);
            }
          }
        } else {
          // Fetch current user's data
          try {
            const profileResponse = await api.get('/auth/profile');
            userData = profileResponse.data;

            // Also fetch from GSC API if profileId is available
            if (userData?.profileId && reservationId) {
              try {
                const gscResponse = await api.get(`/users/guest-profile-from-gsc/${userData.profileId}`, {
                  params: { reservationId: reservationId }
                });
                gscData = gscResponse.data;
              } catch (error) {
                console.error('Error fetching GSC profile data:', error);
                // Continue without GSC data
              }
            }
          } catch (error) {
            console.error('Error fetching current user profile:', error);
          }
        }

        // Merge userData and gscData, prioritizing GSC data for fields that are null/empty in userData
        const mergedData: any = { ...userData };

        if (gscData) {
          // Use GSC data if userData field is empty/null, otherwise keep userData
          mergedData.firstName = userData?.firstName || gscData.firstName || '';
          mergedData.lastName = userData?.lastName || gscData.lastName || '';
          mergedData.email = userData?.email || gscData.email || '';
          mergedData.phoneNumber = userData?.phoneNumber || gscData.phoneNumber || '';
          mergedData.birthdate = userData?.birthdate ? (userData.birthdate.split('T')[0]) : (gscData.birthdate || '');
          mergedData.nationalId = userData?.nationalId || gscData.nationalId || '';
          mergedData.nationality = userData?.nationality || gscData.nationality || '';
          mergedData.country = userData?.country || gscData.country || '';
          mergedData.city = userData?.city || gscData.city || '';
          mergedData.postCode = userData?.postCode || gscData.postCode || '';
          mergedData.sex = userData?.sex || gscData.sex || '';
          mergedData.passportId = userData?.passportId || gscData.passportId || '';
        }

        // Pre-fill form with merged data
        if (mergedData) {
          // If country is empty but nationality is filled, use nationality as country
          const countryValue = mergedData.country || mergedData.nationality || '';

          // Check if nationalId and passportId came from backend (not empty)
          const nationalIdValue = mergedData.nationalId || '';
          const passportIdValue = mergedData.passportId || '';
          
          setIsNationalIdFromBackend(!!nationalIdValue && nationalIdValue.trim() !== '');
          setIsPassportIdFromBackend(!!passportIdValue && passportIdValue.trim() !== '');

          setFormData({
            email: mergedData.email || '',
            phoneNumber: mergedData.phoneNumber || '',
            birthdate: mergedData.birthdate || '',
            nationalId: nationalIdValue,
            nationality: mergedData.nationality || '',
            country: countryValue,
            city: mergedData.city || '',
            postCode: mergedData.postCode || '',
            sex: mergedData.sex || '',
            passportId: passportIdValue,
            productInterests: mergedData.productInterests || [],
            allergies: mergedData.allergies || [],
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchUserData();
  }, [targetProfileId, reservationId, hotelId]);

  // Get country name based on current language
  const getCountryName = (country: typeof countries[0]) => {
    const lang = i18n.language.split('-')[0];
    switch (lang) {
      case 'tr': return country.nameTr || country.name;
      case 'de': return country.nameDe || country.name;
      case 'fr': return country.nameFr || country.name;
      case 'ru': return country.nameRu || country.name;
      case 'ar': return country.nameAr || country.name;
      default: return country.name;
    }
  };

  // Validation functions
  const validatePhone = (value: string): boolean => {
    const phoneRegex = /^\+?[0-9]*$/;
    if (value.includes(' ')) {
      return false;
    }
    if (value.startsWith('+') && value.length > 1) {
      return phoneRegex.test(value);
    }
    if (!value.startsWith('+')) {
      return phoneRegex.test(value);
    }
    return true;
  };

  const validateBirthdate = (value: string): boolean => {
    if (!value) return true;
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  };

  const handleChange = (field: string, value: any) => {
    // Prevent editing if field came from backend
    if (field === 'nationalId' && isNationalIdFromBackend) {
      return;
    }
    if (field === 'passportId' && isPassportIdFromBackend) {
      return;
    }

    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (field === 'phoneNumber') {
      let processedValue = value;
      processedValue = processedValue.replace(/\s/g, '');
      if (processedValue.includes('+') && processedValue.indexOf('+') !== 0) {
        processedValue = processedValue.replace(/\+/g, '');
      }
      if (processedValue && !validatePhone(processedValue)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('validation.phoneFormat')
        }));
        return;
      }
      value = processedValue;
    }

    if (field === 'birthdate') {
      if (value && !validateBirthdate(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('validation.birthDateFuture')
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  // Field labels mapping for dialog
  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      email: t('login.email') || 'Email',
      phoneNumber: t('login.phoneNumber') || 'Phone Number',
      birthdate: t('login.birthdate') || 'Birthdate',
      nationality: t('login.nationality') || 'Nationality',
      country: t('login.country') || 'Country',
      city: t('login.city') || 'City',
      postCode: t('login.postCode') || 'Post Code',
      sex: t('login.gender') || 'Gender',
      passportId: t('login.passportId') || 'Passport ID',
      nationalId: t('login.nationalId') || 'National ID',
    };
    return labels[field] || field;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setFieldErrors({});

    // Check all required fields
    const missing: string[] = [];

    if (!formData.email || !formData.email.trim()) {
      missing.push('email');
    }
    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      missing.push('phoneNumber');
    }
    if (!formData.birthdate || !formData.birthdate.trim()) {
      missing.push('birthdate');
    }
    if (!formData.nationality || !formData.nationality.trim()) {
      missing.push('nationality');
    }
    if (!formData.country || !formData.country.trim()) {
      missing.push('country');
    }
    if (!formData.city || !formData.city.trim()) {
      missing.push('city');
    }
    if (!formData.postCode || !formData.postCode.trim()) {
      missing.push('postCode');
    }
    if (!formData.sex || !formData.sex.trim()) {
      missing.push('sex');
    }
    // National ID (T.C.) is required for Turkish nationality; passport otherwise.
    const isTR = isTurkishNationality(formData.nationality);
    if (formData.nationality && formData.nationality.trim()) {
      if (isTR) {
        if (!formData.nationalId || !formData.nationalId.trim()) {
          missing.push('nationalId');
        }
      } else if (!formData.passportId || !formData.passportId.trim()) {
        missing.push('passportId');
      }
    }

    // If any fields are missing, show dialog
    if (missing.length > 0) {
      setMissingFields(missing);
      setMissingFieldsDialogOpen(true);
      return;
    }

    // Validate field formats
    const errors: Record<string, string> = {};

    if (formData.email && !validateEmail(formData.email)) {
      errors.email = t('validation.invalidEmail');
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      errors.phoneNumber = t('validation.invalidPhoneFormat');
    }

    if (formData.birthdate && !validateBirthdate(formData.birthdate)) {
      errors.birthdate = t('validation.birthDateFuture');
    }

    if (formData.nationality && !countries.find(c => c.code === formData.nationality)) {
      errors.nationality = t('validation.selectValidNationality');
    }

    if (formData.country && !countries.find(c => c.code === formData.country)) {
      errors.country = t('validation.selectValidCountry');
    }

    // Turkish ID / passport format validation (based on nationality)
    if (isTR) {
      if (formData.nationalId && !validateTCKN(formData.nationalId)) {
        errors.nationalId = t('validation.invalidTckn');
      }
    } else if (formData.passportId && !validatePassport(formData.passportId, formData.nationality)) {
      errors.passportId = t('validation.invalidPassport');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!hotelId || !reservationId) {
      setError(t('login.failedToAddGuest') || 'Missing required parameters');
      return;
    }

    setIsLoading(true);

    try {
      const updateData: any = {};

      // Only include non-empty fields
      if (formData.email) updateData.email = formData.email;
      if (formData.phoneNumber) updateData.phoneNumber = formData.phoneNumber;
      if (formData.birthdate) updateData.birthdate = formData.birthdate;
      if (formData.nationalId) updateData.nationalId = formData.nationalId;
      if (formData.nationality) updateData.nationality = formData.nationality;
      if (formData.country) updateData.country = formData.country;
      if (formData.city) updateData.city = formData.city;
      if (formData.postCode) updateData.postCode = formData.postCode;
      if (formData.sex) updateData.sex = formData.sex;
      if (formData.passportId) updateData.passportId = formData.passportId;

      // Note: productInterests and allergies are not yet supported in backend
      // TODO: Add these fields to User entity and UpdateUserDto when needed

      if (targetProfileId) {
        // Update another guest's profile using guest-accessible endpoint
        await api.patch(`/users/guest-by-profile/${targetProfileId}`, updateData);
      } else {
        // Update current user's profile
        await api.patch('/users/profile', updateData);

        // Keep AuthContext in sync so ProtectedRoute does not bounce back here
        await refreshUserProfile();
      }

      setSuccess(true);

      if (targetProfileId) {
        setTimeout(() => {
          const params = new URLSearchParams({
            hotelId: hotelId!,
            reservationId: reservationId!,
          });
          if (profileId) params.append('profileId', profileId);
          if (surname) params.append('surname', surname);
          if (redirectToChatParam) params.append('redirectToChat', 'true');
          navigate(`/guest-onboarding?${params.toString()}`);
        }, 1500);
        return;
      }

      try {
        const usersResponse = await api.get('/users/reservation-guests', {
          params: { reservationId },
        });
        const roomGuests = usersResponse.data || [];
        const currentProfile = profileId || user?.profileId;
        const incomplete = roomGuests.find((guest: any) => {
          if (!guest?.profileId || guest.profileId === currentProfile) return false;
          if (guest.role && guest.role !== 'guest' && guest.role !== 'GUEST') return false;
          return isGuestProfileIncomplete(guest);
        });
        if (incomplete?.profileId) {
          setOtherIncompleteGuest({
            firstName: incomplete.firstName || '',
            lastName: incomplete.lastName || '',
            profileId: incomplete.profileId,
          });
        } else {
          setTimeout(() => navigateToOnboarding(), 1200);
        }
      } catch {
        setTimeout(() => navigateToOnboarding(), 1200);
      }
    } catch (error: any) {
      console.error('Failed to update guest info:', error);
      setError(error.response?.data?.message || t('login.failedToAddGuest') || 'Failed to update guest information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToOnboarding = () => {
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (redirectToChatParam) params.append('redirectToChat', 'true');
    navigate(`/guest-onboarding?${params.toString()}`);
  };

  const handleUpdateOtherGuest = () => {
    if (!otherIncompleteGuest?.profileId) return;
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
      targetProfileId: otherIncompleteGuest.profileId,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (redirectToChatParam) params.append('redirectToChat', 'true');
    setSuccess(false);
    setOtherIncompleteGuest(null);
    navigate(`/complete-guest-info?${params.toString()}`);
  };

  const handleBack = () => {
    navigateToOnboarding();
  };

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.8)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark ? '0 8px 22px rgba(0, 0, 0, 0.45)' : '0 4px 12px rgba(0, 174, 239, 0.12)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark ? '0 12px 30px rgba(0, 174, 239, 0.3)' : '0 8px 20px rgba(0, 174, 239, 0.18)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2.5,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
        transition: 'all 0.3s ease',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: secondaryTextColor,
      '&.Mui-focused': {
        color: accentColor,
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: primaryTextColor,
    },
  };

  return (
    <>
      <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: paperBg,
            backdropFilter: 'blur(10px)',
            border: paperBorder,
            color: primaryTextColor,
            boxShadow: isDark
              ? '0 25px 70px rgba(0, 0, 0, 0.6)'
              : '0 30px 80px rgba(15, 23, 42, 0.12)',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={isLoading || success}
              sx={{
                mb: 2,
                textTransform: 'none',
                color: accentColor,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                },
              }}
            >
              {t('login.back')}
            </Button>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: accentColor }}>
                {targetProfileId && targetGuestName
                  ? `${t('onboarding.completeInfo') || 'Complete Missing Information'} - ${targetGuestName}`
                  : t('onboarding.completeInfo') || 'Complete Missing Information'}
              </Typography>
              <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                {targetProfileId
                  ? t('onboarding.completeInfoDescOther') || 'Please complete the missing information for this guest'
                  : t('onboarding.completeInfoDesc') || 'Please continue filling out the form for guests with missing information'}
              </Typography>
              {verifyFlow && (
                <Alert severity="info" sx={{ mt: 2, textAlign: 'left', borderRadius: 2 }}>
                  {t('onboarding.verifyFlowBanner')}
                </Alert>
              )}
            </Box>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)',
                animation: 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
              }}
            >
              {t('onboarding.infoUpdated') || 'Your information has been successfully updated!'}
            </Alert>
          )}

          {success && otherIncompleteGuest && !targetProfileId && (
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                {t('onboarding.updateOthersInfoDesc')}
              </Typography>
              <Button
                variant="contained"
                onClick={handleUpdateOtherGuest}
                sx={{
                  py: 1.5,
                  backgroundColor: accentColor,
                  '&:hover': { backgroundColor: accentHover },
                }}
              >
                {t('onboarding.updateOthersInfo')} — {otherIncompleteGuest.firstName} {otherIncompleteGuest.lastName}
              </Button>
              <Button variant="outlined" onClick={navigateToOnboarding} sx={{ py: 1.5 }}>
                {t('onboarding.continueToOnboarding')}
              </Button>
            </Box>
          )}

          {isLoadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : success && otherIncompleteGuest && !targetProfileId ? null : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label={t('login.email')}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={isLoading || success}
                    error={!!fieldErrors.email || !formData.email || !formData.email.trim()}
                    helperText={fieldErrors.email}
                    sx={textFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: accentColor, fontSize: '1.25rem' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label={t('login.phoneNumber')}
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value.replace(/\s/g, '');
                      if (value.includes('+') && value.indexOf('+') !== 0) {
                        value = value.replace(/\+/g, '');
                      }
                      handleChange('phoneNumber', value);
                    }}
                    disabled={isLoading || success}
                    error={!!fieldErrors.phoneNumber || !formData.phoneNumber || !formData.phoneNumber.trim()}
                    helperText={fieldErrors.phoneNumber || (t('login.phoneNumber') === 'Phone Number' ? 'Format: +1234567890' : 'Format: +905551234567')}
                    sx={textFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: accentColor, fontSize: '1.25rem' }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 20,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label={t('login.birthdate')}
                    InputLabelProps={{ shrink: true }}
                    value={formData.birthdate}
                    onChange={(e) => handleChange('birthdate', e.target.value)}
                    disabled={isLoading || success}
                    error={!!fieldErrors.birthdate || !formData.birthdate || !formData.birthdate.trim()}
                    helperText={fieldErrors.birthdate}
                    sx={textFieldSx}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required={isTurkishNationality(formData.nationality)}
                    label={t('login.nationalId')}
                    value={formData.nationalId}
                    onChange={(e) => handleChange('nationalId', e.target.value)}
                    disabled={isLoading || success || isNationalIdFromBackend}
                    error={!!fieldErrors.nationalId || (isTurkishNationality(formData.nationality) && (!formData.nationalId || !formData.nationalId.trim()))}
                    helperText={fieldErrors.nationalId}
                    inputProps={{ maxLength: 11 }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={!!fieldErrors.nationality || !formData.nationality || !formData.nationality.trim()} sx={textFieldSx}>
                    <InputLabel sx={{ color: secondaryTextColor, '&.Mui-focused': { color: accentColor } }}>{t('login.nationality')}</InputLabel>
                    <Select
                      value={formData.nationality}
                      label={t('login.nationality')}
                      onChange={(e) => handleChange('nationality', e.target.value)}
                      disabled={isLoading || success}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        color: primaryTextColor,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            mt: 1,
                            boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 174, 239, 0.1)',
                            backdropFilter: 'blur(20px)',
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              borderRadius: 2,
                              mx: 1,
                              my: 0.5,
                              transition: 'all 0.2s ease',
                              color: primaryTextColor,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                                transform: 'translateX(4px)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.25)' : 'rgba(0, 174, 239, 0.15)',
                                color: accentColor,
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.3)' : 'rgba(0, 174, 239, 0.2)',
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {getCountryName(country)}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.nationality && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {fieldErrors.nationality}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={!formData.sex || !formData.sex.trim()} sx={textFieldSx}>
                    <InputLabel sx={{ fontWeight: 600, color: secondaryTextColor, '&.Mui-focused': { color: accentColor, fontWeight: 700 } }}>{t('login.gender')}</InputLabel>
                    <Select
                      value={formData.sex}
                      label={t('login.gender')}
                      onChange={(e) => handleChange('sex', e.target.value)}
                      disabled={isLoading || success}
                      sx={{
                        ...textFieldSx,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        color: primaryTextColor,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            mt: 1,
                            boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 174, 239, 0.1)',
                            backdropFilter: 'blur(20px)',
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            '& .MuiMenuItem-root': {
                              borderRadius: 2,
                              mx: 1,
                              my: 0.5,
                              transition: 'all 0.2s ease',
                              color: primaryTextColor,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                                transform: 'translateX(4px)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.25)' : 'rgba(0, 174, 239, 0.15)',
                                color: accentColor,
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.3)' : 'rgba(0, 174, 239, 0.2)',
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="M">{t('login.male')}</MenuItem>
                      <MenuItem value="F">{t('login.female')}</MenuItem>
                      <MenuItem value="Other">{t('login.other')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={!!fieldErrors.country || !formData.country || !formData.country.trim()} sx={textFieldSx}>
                    <InputLabel sx={{ color: secondaryTextColor, '&.Mui-focused': { color: accentColor } }}>{t('login.country')}</InputLabel>
                    <Select
                      value={formData.country}
                      label={t('login.country')}
                      onChange={(e) => handleChange('country', e.target.value)}
                      disabled={isLoading || success}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: accentColor,
                        },
                        color: primaryTextColor,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            mt: 1,
                            boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 174, 239, 0.1)',
                            backdropFilter: 'blur(20px)',
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              borderRadius: 2,
                              mx: 1,
                              my: 0.5,
                              transition: 'all 0.2s ease',
                              color: primaryTextColor,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                                transform: 'translateX(4px)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.25)' : 'rgba(0, 174, 239, 0.15)',
                                color: accentColor,
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.3)' : 'rgba(0, 174, 239, 0.2)',
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {getCountryName(country)}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.country && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {fieldErrors.country}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label={t('login.city')}
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    disabled={isLoading || success}
                    error={!formData.city || !formData.city.trim()}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label={t('login.postCode')}
                    value={formData.postCode}
                    onChange={(e) => handleChange('postCode', e.target.value)}
                    disabled={isLoading || success}
                    error={!formData.postCode || !formData.postCode.trim()}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required={!isTurkishNationality(formData.nationality)}
                    fullWidth
                    label={t('login.passportId')}
                    value={formData.passportId}
                    onChange={(e) => handleChange('passportId', e.target.value)}
                    disabled={isLoading || success || isPassportIdFromBackend}
                    error={!!fieldErrors.passportId || (!isTurkishNationality(formData.nationality) && (!formData.passportId || !formData.passportId.trim()))}
                    helperText={fieldErrors.passportId}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: primaryTextColor,
                        mb: 2,
                        fontSize: '0.95rem',
                      }}
                    >
                      {t('login.productInterests')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.6)',
                        border: isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid rgba(0, 0, 0, 0.08)',
                        minHeight: 80,
                      }}
                    >
                      {productInterestOptions.map((option) => {
                        const isSelected = formData.productInterests.includes(option.value);
                        return (
                          <Chip
                            key={option.value}
                            icon={option.icon}
                            label={option.label}
                            onClick={() => {
                              if (isLoading || success) return;
                              const newInterests = isSelected
                                ? formData.productInterests.filter((v) => v !== option.value)
                                : [...formData.productInterests, option.value];
                              handleChange('productInterests', newInterests);
                            }}
                            disabled={isLoading || success}
                            sx={{
                              height: 48,
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              backgroundColor: isSelected
                                ? option.color
                                : (isDark ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.9)'),
                              color: isSelected ? '#ffffff' : primaryTextColor,
                              border: isSelected
                                ? `2px solid ${option.color}`
                                : (isDark ? '2px solid rgba(255, 255, 255, 0.15)' : '2px solid rgba(0, 0, 0, 0.12)'),
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${option.color}40`,
                                backgroundColor: isSelected ? option.color : (isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 1)'),
                              },
                              '& .MuiChip-icon': {
                                color: isSelected ? '#ffffff' : option.color,
                                fontSize: '1.5rem',
                              },
                              '&:disabled': {
                                opacity: 0.6,
                                cursor: 'not-allowed',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: primaryTextColor,
                        mb: 2,
                        fontSize: '0.95rem',
                      }}
                    >
                      {t('login.allergies')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.6)',
                        border: isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid rgba(0, 0, 0, 0.08)',
                        minHeight: 80,
                      }}
                    >
                      {allergyOptions.map((option) => {
                        const isSelected = formData.allergies.includes(option.value);
                        return (
                          <Chip
                            key={option.value}
                            icon={option.icon}
                            label={option.label}
                            onClick={() => {
                              if (isLoading || success) return;
                              const newAllergies = isSelected
                                ? formData.allergies.filter((v) => v !== option.value)
                                : [...formData.allergies, option.value];
                              handleChange('allergies', newAllergies);
                            }}
                            disabled={isLoading || success}
                            sx={{
                              height: 48,
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              backgroundColor: isSelected
                                ? option.color
                                : (isDark ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.9)'),
                              color: isSelected ? '#ffffff' : primaryTextColor,
                              border: isSelected
                                ? `2px solid ${option.color}`
                                : (isDark ? '2px solid rgba(255, 255, 255, 0.15)' : '2px solid rgba(0, 0, 0, 0.12)'),
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${option.color}40`,
                                backgroundColor: isSelected ? option.color : (isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 1)'),
                              },
                              '& .MuiChip-icon': {
                                color: isSelected ? '#ffffff' : option.color,
                                fontSize: '1.5rem',
                              },
                              '&:disabled': {
                                opacity: 0.6,
                                cursor: 'not-allowed',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={isLoading || success}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)',
                    borderWidth: 1.5,
                    color: secondaryTextColor,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.6)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.35)' : '#9ca3af',
                      borderWidth: 2,
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-1px)',
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  {t('login.back')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || success}
                  sx={{
                    px: 4,
                    py: 1.5,
                    backgroundColor: accentColor,
                    boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
                    '&:hover': {
                      backgroundColor: accentHover,
                      boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                      color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : (t('onboarding.save') || 'Save')}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Missing Fields Dialog */}
      <Dialog
        open={missingFieldsDialogOpen}
        onClose={() => setMissingFieldsDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
            background: paperBg,
            border: paperBorder,
          },
        }}
      >
        <DialogTitle sx={{ color: primaryTextColor, fontWeight: 700 }}>
          {t('validation.pleaseFillRequiredFields') || 'Please fill required fields'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: secondaryTextColor, mb: 2 }}>
            {t('validation.missingFieldsMessage') || 'The following fields are required:'}
          </Typography>
          <List dense>
            {missingFields.map((field) => (
              <ListItem key={field} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`• ${getFieldLabel(field)}`}
                  primaryTypographyProps={{
                    sx: { color: primaryTextColor, fontWeight: 500 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setMissingFieldsDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: accentColor,
              '&:hover': {
                backgroundColor: accentHover,
              },
            }}
          >
            {t('common.ok') || 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompleteGuestInfoPage;
